import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Building2, Wallet, FileText, MapPin, ClipboardCheck,
  HardHat, Layers, Hammer, Cpu, KeyRound, ShieldCheck, Flame, ParkingCircle,
  CheckCircle2, Phone, Calendar
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../contexts/LanguageContext';

const t = (lang, fr, en) => (lang === 'fr' ? fr : en);

const ConstructionImmeublePage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const heroImg =
    'https://images.unsplash.com/photo-1486325212027-8081e485255e?auto=format&fit=crop&w=1920&q=70';

  const steps = [
    { n: '01', Icon: ClipboardCheck, title: t(language, 'Programme & faisabilité', 'Brief & feasibility'),
      text: t(language,
        "Définition du programme (résidentiel, bureaux, commercial), étude de marché à Kinshasa, capacité du site, calcul de la SHON/SHOB et préfaisabilité financière.",
        'Brief definition (residential, offices, commercial), Kinshasa market study, site capacity, GFA calculation and financial pre-feasibility.') },
    { n: '02', Icon: MapPin, title: t(language, 'Terrain & zonage', 'Land & zoning'),
      text: t(language,
        "Sélection d'une parcelle conforme au plan d'urbanisme de Kinshasa, vérification des servitudes, hauteur autorisée, COS et CES, étude géotechnique poussée.",
        'Selection of a plot compliant with the Kinshasa urban plan, easement check, allowed height, plot ratios, in-depth geotechnical study.') },
    { n: '03', Icon: FileText, title: t(language, 'Études techniques', 'Technical studies'),
      text: t(language,
        "Architecture, structure béton armé R+2 à R+10, études MEP (électricité, plomberie, climatisation, ascenseurs, désenfumage), notes de calcul sismique et vent.",
        'Architecture, reinforced concrete structure R+2 to R+10, MEP studies (electrical, plumbing, HVAC, lifts, smoke extraction), seismic and wind calculation notes.') },
    { n: '04', Icon: ShieldCheck, title: t(language, 'Permis & conformité', 'Permit & compliance'),
      text: t(language,
        "Dépôt du permis de bâtir auprès de l'Hôtel de Ville, avis du service d'urbanisme, conformité incendie (Protection civile), accessibilité PMR et environnement.",
        'Building permit filed at Kinshasa City Hall, urban planning approval, fire safety compliance (Civil Protection), accessibility and environmental review.') },
    { n: '05', Icon: HardHat, title: t(language, 'Fondations spéciales', 'Special foundations'),
      text: t(language,
        "Selon la portance du sol kinois : pieux forés, semelles isolées renforcées ou radier général. Coulage par phase pour respecter le séchage en saison des pluies.",
        'Depending on Kinshasa soil bearing: bored piles, reinforced isolated footings or raft foundation. Phased pouring to manage curing during the rainy season.') },
    { n: '06', Icon: Layers, title: t(language, 'Gros œuvre par étage', 'Structural shell per floor'),
      text: t(language,
        "Élévation des planchers, voiles porteurs, poteaux et poutres ; coordination grue / monte-matériaux ; tests de résistance du béton à 7, 14 et 28 jours.",
        'Floor slabs, shear walls, columns and beams; crane and material hoist coordination; concrete strength tests at 7, 14 and 28 days.') },
    { n: '07', Icon: Cpu, title: t(language, 'Second œuvre & équipements', 'Second fix & equipment'),
      text: t(language,
        "Cloisonnement, gaines techniques, ascenseurs, climatisation centralisée VRV, GTB / GTC, système incendie, vidéosurveillance, contrôle d'accès, raccordement SNEL/REGIDESO.",
        'Partitioning, MEP shafts, lifts, VRV central air conditioning, BMS, fire system, CCTV, access control, SNEL/REGIDESO hook-ups.') },
    { n: '08', Icon: KeyRound, title: t(language, 'Réception & exploitation', 'Handover & operations'),
      text: t(language,
        "Tests d'étanchéité, mise en service des équipements, formation du syndic ou du facility manager, remise du DOE et garanties (parfait achèvement, biennale, décennale).",
        'Watertightness tests, equipment commissioning, training of building manager, DOE handover and warranties (defects, biennial, decennial).') },
  ];

  const prepBlocks = [
    { Icon: Wallet, title: t(language, 'Montage financier', 'Financial structuring'),
      points: [
        t(language, "Plan de financement : fonds propres, project finance, dette bancaire (BCDC, Rawbank, TMB, Equity).", 'Funding plan: equity, project finance, bank debt (BCDC, Rawbank, TMB, Equity).'),
        t(language, "Étude de rentabilité (TRI, VAN, payback) pour les bâtiments locatifs ou commerciaux.", 'Profitability study (IRR, NPV, payback) for rental or commercial buildings.'),
        t(language, "Provision de 12–18 % pour aléas, fluctuations forex et inflation.", '12–18 % contingency for unforeseen events, forex fluctuations and inflation.'),
        t(language, "Sécurisation des décaissements par phase contre attestations d'avancement.", 'Phased disbursements secured against progress reports.'),
      ] },
    { Icon: MapPin, title: t(language, 'Terrain & autorisations', 'Land & approvals'),
      points: [
        t(language, "Choix d'un site desservi (route asphaltée, SNEL, REGIDESO, fibre).", 'Choose a serviced site (paved road, SNEL, REGIDESO, fibre).'),
        t(language, "Vérifier la conformité au schéma directeur de Kinshasa (zonage).", 'Check compliance with the Kinshasa master plan (zoning).'),
        t(language, "Étude de sol classe G2/G3 selon la hauteur projetée.", 'G2/G3 class soil study depending on planned height.'),
        t(language, "Permis de bâtir + étude d'impact environnemental si nécessaire.", 'Building permit + environmental impact assessment if required.'),
      ] },
    { Icon: ShieldCheck, title: t(language, 'Normes & sécurité', 'Standards & safety'),
      points: [
        t(language, "Respect du code de la construction et des normes ERP en RDC.", 'Compliance with the construction code and ERP standards in DRC.'),
        t(language, "Désenfumage, sprinklers, RIA, issues de secours signalées.", 'Smoke extraction, sprinklers, fire hose reels, signed emergency exits.'),
        t(language, "Plan HSE chantier validé, EPI, INPP et CNSS pour la main-d'œuvre.", 'Approved site HSE plan, PPE, INPP and CNSS for the workforce.'),
        t(language, "Contrôle technique indépendant (CT) sur structure et incendie.", 'Independent technical inspection (CT) on structure and fire safety.'),
      ] },
    { Icon: Building2, title: t(language, 'Choix du maître d\u2019œuvre', 'Choosing the project lead'),
      points: [
        t(language, "Bureau d'études pluridisciplinaire (architecte + BET structure + MEP).", 'Multidisciplinary firm (architect + structural BET + MEP).'),
        t(language, "Références d'immeubles livrés à Kinshasa (R+3 minimum).", 'References of buildings delivered in Kinshasa (R+3 minimum).'),
        t(language, "Contrat clair avec planning Gantt, jalons et pénalités de retard.", 'Clear contract with Gantt schedule, milestones and delay penalties.'),
        t(language, "Reporting hebdomadaire et réunions de chantier formalisées.", 'Weekly reporting and formalised site meetings.'),
      ] },
  ];

  const rdcSpecifics = [
    { Icon: ParkingCircle, title: t(language, 'Stationnement obligatoire', 'Mandatory parking'),
      text: t(language,
        "Le règlement d'urbanisme de Kinshasa impose un ratio de stationnement par logement ou par 100 m² de surface de bureaux. Anticiper un parking en sous-sol ou semi-enterré dès l'esquisse.",
        'Kinshasa urban regulations require a parking ratio per dwelling or per 100 m² of office space. Plan an underground or semi-buried parking from concept stage.') },
    { Icon: Flame, title: t(language, 'Sécurité incendie', 'Fire safety'),
      text: t(language,
        "Pour tout bâtiment R+3 et plus : escaliers de secours pressurisés, désenfumage mécanique, détection incendie adressable, système d'extinction (sprinklers + RIA) et alarme générale.",
        'For any R+3 or higher building: pressurised escape stairs, mechanical smoke extraction, addressable fire detection, extinguishing system (sprinklers + fire hose reels) and general alarm.') },
    { Icon: Cpu, title: t(language, 'Équipements techniques', 'Technical equipment'),
      text: t(language,
        "Ascenseurs Schindler/Otis/Kone homologués, climatisation VRV ou centrale eau glacée, GTB pour piloter consommation et confort, onduleurs pour pallier les coupures SNEL.",
        'Approved Schindler/Otis/Kone lifts, VRV or chilled-water central HVAC, BMS to manage consumption and comfort, UPS to mitigate SNEL outages.') },
    { Icon: ShieldCheck, title: t(language, 'Garanties & assurances', 'Warranties & insurance'),
      text: t(language,
        "Assurance Tous Risques Chantier (TRC), assurance dommage-ouvrage (DO), garanties parfait achèvement (1 an), de bon fonctionnement (2 ans) et décennale (10 ans).",
        'All-risk site insurance (TRC), damage insurance (DO), defects warranty (1 year), good-functioning warranty (2 years) and decennial warranty (10 years).') },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-[240px]" data-testid="construction-immeuble-page">
        <section className="relative h-[360px] md:h-[460px] overflow-hidden" data-testid="immeuble-hero">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${heroImg})` }} />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/30" />
          <div className="relative max-w-6xl mx-auto px-6 h-full flex flex-col justify-center">
            <button onClick={() => navigate('/')} className="self-start inline-flex items-center gap-2 text-white/85 hover:text-orange-400 mb-6 transition-colors" data-testid="immeuble-back-btn">
              <ArrowLeft size={18} />
              <span className="font-semibold">{t(language, "Retour à l'accueil", 'Back to home')}</span>
            </button>
            <h1 className="text-white text-3xl sm:text-4xl md:text-6xl font-bold leading-tight max-w-3xl">
              {t(language, "Construire votre immeuble à Kinshasa", 'Build your building in Kinshasa')}
            </h1>
            <p className="text-white/85 mt-4 max-w-2xl text-base md:text-lg">
              {t(language,
                "Bureaux, logements, retail ou mixte : maîtrisez chaque étape, du programme au facility management.",
                'Offices, residential, retail or mixed-use: master every step, from brief to facility management.')}
            </p>
          </div>
        </section>

        <section className="py-16 md:py-20" data-testid="immeuble-intro">
          <div className="max-w-5xl mx-auto px-6 text-center">
            <h2 className="text-2xl md:text-4xl font-bold mb-6" style={{ color: '#702963' }}>
              {t(language,
                "Un immeuble durable se prépare bien avant le premier coup de pioche",
                'A durable building is prepared long before the first dig')}
            </h2>
            <p className="text-base md:text-lg text-gray-700 leading-relaxed">
              {t(language,
                "Construire un immeuble à Kinshasa exige une coordination fine entre architecture, ingénierie structure, lots techniques, exploitation future et conformité réglementaire. GLOBAL CONSTRUCTION & INGÉNIERIE SARL pilote l'ensemble du cycle, du programme client jusqu'à la mise en exploitation, en intégrant les contraintes propres au climat tropical, à la qualité du sol et à la disponibilité des réseaux.",
                'Building a structure in Kinshasa requires careful coordination between architecture, structural engineering, technical lots, future operations and regulatory compliance. GLOBAL CONSTRUCTION & INGÉNIERIE SARL drives the full cycle, from client brief to operations, factoring in tropical climate, soil quality and utility availability.')}
            </p>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-gray-50" data-testid="immeuble-prep">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-4xl font-bold mb-3" style={{ color: '#702963' }}>
                {t(language, "Préparation : 4 piliers à sécuriser", '4 pillars to secure')}
              </h2>
              <div className="mx-auto h-[3px] w-20 bg-orange-500 rounded-full" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {prepBlocks.map((b, idx) => {
                const Icon = b.Icon;
                return (
                  <div key={idx} className="bg-white border border-gray-200 rounded-sm p-7 md:p-8 shadow-sm hover:shadow-xl transition-all duration-300" data-testid={`immeuble-prep-${idx}`}>
                    <div className="flex items-center gap-4 mb-5">
                      <div className="w-14 h-14 flex items-center justify-center bg-orange-50 rounded-full text-orange-600 flex-shrink-0">
                        <Icon className="w-7 h-7" strokeWidth={2} />
                      </div>
                      <h3 className="text-lg md:text-xl font-bold text-gray-900">{b.title}</h3>
                    </div>
                    <ul className="space-y-3">
                      {b.points.map((p, i) => (
                        <li key={i} className="flex items-start gap-3 text-gray-700 text-sm md:text-base leading-relaxed">
                          <CheckCircle2 className="w-5 h-5 text-[#702963] flex-shrink-0 mt-0.5" />
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24" data-testid="immeuble-steps">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-14">
              <h2 className="text-2xl md:text-4xl font-bold mb-3" style={{ color: '#702963' }}>
                {t(language, "8 étapes clés d'un projet d'immeuble", 'The 8 key steps of a building project')}
              </h2>
              <div className="mx-auto mt-4 h-[3px] w-20 bg-orange-500 rounded-full" />
            </div>
            <div className="relative">
              <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gray-200 -translate-x-1/2" />
              <div className="space-y-10 md:space-y-16">
                {steps.map((s, i) => {
                  const Icon = s.Icon;
                  const left = i % 2 === 0;
                  return (
                    <div key={s.n} className="md:grid md:grid-cols-2 md:gap-12 items-center" data-testid={`immeuble-step-${s.n}`}>
                      <div className={`${left ? 'md:order-1 md:text-right' : 'md:order-2'} mb-4 md:mb-0`}>
                        <div className={`flex items-start gap-4 ${left ? 'md:flex-row-reverse md:text-right' : ''}`}>
                          <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-[#702963] text-white rounded-full flex-shrink-0">
                            <Icon className="w-6 h-6 md:w-7 md:h-7" strokeWidth={2} />
                          </div>
                          <div>
                            <span className="text-orange-500 font-bold text-sm tracking-widest">{t(language, 'ÉTAPE', 'STEP')} {s.n}</span>
                            <h3 className="text-lg md:text-2xl font-bold text-gray-900 mt-1 mb-2">{s.title}</h3>
                            <p className="text-gray-700 text-sm md:text-base leading-relaxed">{s.text}</p>
                          </div>
                        </div>
                      </div>
                      <div className={`hidden md:block ${left ? 'md:order-2' : 'md:order-1'}`} />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-gray-50" data-testid="immeuble-rdc">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-4xl font-bold mb-3" style={{ color: '#702963' }}>
                {t(language, "Spécificités à Kinshasa", 'Kinshasa specifics')}
              </h2>
              <div className="mx-auto mt-4 h-[3px] w-20 bg-orange-500 rounded-full" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {rdcSpecifics.map((r, i) => {
                const Icon = r.Icon;
                return (
                  <div key={i} className="bg-white p-6 md:p-7 border-t-4 border-[#702963] shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300" data-testid={`immeuble-rdc-card-${i}`}>
                    <div className="w-12 h-12 flex items-center justify-center bg-orange-50 rounded-full text-orange-600 mb-4">
                      <Icon className="w-6 h-6" strokeWidth={2} />
                    </div>
                    <h3 className="text-base md:text-lg font-bold text-gray-900 mb-2">{r.title}</h3>
                    <p className="text-sm md:text-base text-gray-700 leading-relaxed">{r.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20" data-testid="immeuble-duration">
          <div className="max-w-5xl mx-auto px-6">
            <div className="bg-[#702963] text-white p-8 md:p-12 rounded-sm shadow-2xl">
              <div className="flex items-start gap-5">
                <Calendar className="w-10 h-10 text-orange-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl md:text-3xl font-bold mb-3">{t(language, 'Durée indicative', 'Indicative duration')}</h3>
                  <p className="text-white/90 text-base md:text-lg leading-relaxed">
                    {t(language,
                      "Pour un immeuble R+3 à Kinshasa, comptez environ 14 à 20 mois. Pour un R+5 à R+10 avec parking en sous-sol, climatisation centralisée et ascenseurs, prévoyez 22 à 36 mois selon la complexité et la disponibilité des équipements importés.",
                      'For an R+3 building in Kinshasa, allow around 14 to 20 months. For R+5 to R+10 with underground parking, central HVAC and lifts, allow 22 to 36 months depending on complexity and imported equipment availability.')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-white" data-testid="immeuble-cta">
          <div className="max-w-5xl mx-auto px-6 text-center">
            <h2 className="text-2xl md:text-4xl font-bold mb-4" style={{ color: '#702963' }}>
              {t(language, 'Un immeuble à édifier ?', 'A building to erect?')}
            </h2>
            <p className="text-gray-700 text-base md:text-lg mb-8 max-w-2xl mx-auto">
              {t(language,
                "Recevez une note de cadrage et une fourchette budgétaire indicative sous 10 jours ouvrés.",
                'Receive a framing note and an indicative budget range within 10 business days.')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button onClick={() => navigate('/votre-projet')} className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-3 rounded-sm transition-all duration-300 transform hover:scale-105" data-testid="immeuble-cta-project">
                {t(language, 'Démarrer mon projet', 'Start my project')}
                <ArrowRight size={18} />
              </button>
              <a href="tel:+243971327318" className="inline-flex items-center gap-2 border-2 border-[#702963] text-[#702963] font-semibold px-8 py-3 hover:bg-[#702963] hover:text-white transition-all" data-testid="immeuble-cta-call">
                <Phone size={18} />
                {t(language, 'Appelez-nous', 'Call us')}
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ConstructionImmeublePage;
